import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import {
    submitNoPeanutPolicy,
    selectPolicyById,
    selectSubmitLoading
} from '../../store/noPeanutPolicy/noPeanutPolicySlice';
import { handleImageUpload } from '../../api/uploads';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const NoPeanutPolicyFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const existingPolicy = useSelector((state) => selectPolicyById(state, id));
    const submitLoading = useSelector(selectSubmitLoading);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    const signaturePadRef = useRef(null);
    // Tracks whether the parent has actually drawn on the pad (set via
    // onEnd below, which only fires for a real pointer/touch stroke - never
    // from the programmatic fromDataURL() load in the effect below that
    // draws an existing signature onto it). Used at submit time to decide
    // whether there's a new signature to upload, rather than just checking
    // whether the pad looks non-empty - which it will, for as long as an
    // existing signature was successfully pre-loaded onto it, even though
    // nothing new was drawn.
    const signatureDirtyRef = useRef(false);

    // submitLoading (Redux) only reflects the self-contained dispatch branch
    // below - it stays false the whole time when onSubmit is used instead
    // (the parent's onSubmit does the dispatching), which would otherwise
    // leave the Submit button clickable during that async work. Mirrors the
    // same local guard already used in LunchPlanWaiverFormComp.js.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        parent_signature: '',
        parent_signature_date: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        if (isEditMode && existingPolicy) {
            setFormData({
                parent_signature: existingPolicy.parent_signature || '',
                parent_signature_date: existingPolicy.parent_signature_date || new Date().toISOString().split('T')[0]
            });
        }
    }, [isEditMode, existingPolicy]);

    // Draws an existing signature URL directly onto the pad on edit, so it
    // shows up already signed instead of a blank pad next to a separate
    // static preview image. Fetches it as a blob first and hands the pad a
    // same-origin blob: URL rather than a plain cross-origin one - drawing
    // a cross-origin image straight onto the canvas permanently taints it,
    // and every later toDataURL() call (i.e. re-saving this pad) then
    // throws a SecurityError instead of producing an image, even for a
    // resubmit that never touched the signature. Deliberately no fallback
    // to a direct (non-blob) draw if this fetch fails - confirmed
    // elsewhere in this app that doing so, while it does make the pad
    // preview correctly, still leaves toDataURL() throwing on a later
    // submit and breaks it entirely. A signature that doesn't preview here
    // is a much smaller problem than a submission that hard-fails.
    useEffect(() => {
        const url = existingPolicy?.parent_signature;
        if (!isEditMode || !url) return;

        let cancelled = false;
        let attempts = 0;
        let timeoutId;

        const tryDraw = () => {
            attempts += 1;
            const pad = signaturePadRef.current;
            if (!pad) {
                if (attempts < 10) timeoutId = setTimeout(tryDraw, 150);
                return;
            }
            if (!pad.isEmpty()) return;

            fetch(url)
                .then((res) => {
                    if (!res.ok) throw new Error(`Signature fetch failed: ${res.status}`);
                    return res.blob();
                })
                .then((blob) => {
                    if (cancelled || !signaturePadRef.current || !signaturePadRef.current.isEmpty()) return;

                    // react-signature-canvas only sizes the canvas's actual
                    // pixel drawing buffer once, in its own componentDidMount
                    // (from offsetWidth/offsetHeight at that instant) -
                    // clearOnResize={false} is what stops it resyncing that
                    // on a later resize event too (needed so a stray window
                    // resize can't wipe a signature the user just drew). If
                    // the buffer it baked in at mount was stale/undersized
                    // by the time this fetch resolves, drawImage() silently
                    // draws into a too-small buffer and nothing shows up,
                    // even though isEmpty() correctly flips to false.
                    // Re-measure and resync the buffer to the pad's actual
                    // current on-screen size right before drawing.
                    const canvasEl = signaturePadRef.current.getCanvas();
                    if (canvasEl.offsetWidth > 0 && canvasEl.offsetHeight > 0) {
                        const ratio = Math.max(window.devicePixelRatio || 1, 1);
                        canvasEl.width = canvasEl.offsetWidth * ratio;
                        canvasEl.height = canvasEl.offsetHeight * ratio;
                        canvasEl.getContext('2d').scale(ratio, ratio);
                    }

                    const objectUrl = URL.createObjectURL(blob);
                    signaturePadRef.current.fromDataURL(objectUrl, {
                        width: canvasEl.offsetWidth || undefined,
                        height: canvasEl.offsetHeight || undefined
                    });
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
                })
                .catch((e) => {
                    console.log('Could not load existing signature - leaving the pad blank:', e);
                });
        };

        timeoutId = setTimeout(tryDraw, 100);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [isEditMode, existingPolicy?.parent_signature]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
        signatureDirtyRef.current = false;
        // Previously left formData.parent_signature untouched, so clearing
        // only ever cleared the pad visually - the old URL was still what
        // got submitted.
        setFormData(prev => ({ ...prev, parent_signature: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }

        const hasSignaturePadContent = signaturePadRef.current && !signaturePadRef.current.isEmpty();
        const hasExistingSignature = isEditMode && existingPolicy?.parent_signature;

        if (!hasSignaturePadContent && !hasExistingSignature) {
            Swal.fire({
                icon: 'warning',
                title: 'Signature Required',
                text: 'Please provide parent signature.'
            });
            return;
        }

        if (!formData.parent_signature_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Required',
                text: 'Please provide signature date.'
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const submitData = { ...formData };

            // Only re-upload if the pad has something the parent actually
            // drew (signatureDirtyRef) - not just "the pad looks
            // non-empty", which it will for as long as an existing
            // signature is loaded onto it (see the effect above), even
            // when nothing new was drawn.
            if (signatureDirtyRef.current && hasSignaturePadContent) {
                try {
                    const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
                    const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
                    const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
                    const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
                    submitData.parent_signature = signatureUrl;
                } catch (uploadError) {
                    // A tainted canvas (or any other upload failure)
                    // shouldn't hard-fail the whole submission - fall back
                    // to whatever signature was already on file.
                    console.error('Could not upload signature - keeping the existing value:', uploadError);
                    if (hasExistingSignature) {
                        submitData.parent_signature = existingPolicy.parent_signature;
                    }
                }
            } else if (hasExistingSignature) {
                submitData.parent_signature = existingPolicy.parent_signature;
            }

            if (isEditMode) {
                submitData.id = id;
            }

            submitData.student_id = getEffectiveStudentId(profileData);

            // Show loader during submission
            Swal.fire({
                title: 'Processing...',
                html: 'Saving no peanut policy...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Same reasoning as LunchPlanWaiverFormComp.js: when embedded in
            // the profile page's Registration Packet tab, let the host page's
            // onSubmit own dispatch + success feedback + in-place refresh,
            // instead of also self-dispatching and navigating away to
            // /dashboard/no-peanut-policy/list. Standalone add/edit routes
            // never pass onSubmit, so they're unaffected.
            if (onSubmit) {
                await onSubmit(submitData);
                Swal.close();
                signatureDirtyRef.current = false;
            } else {
                await dispatch(submitNoPeanutPolicy(submitData)).unwrap();

                Swal.close();
                signatureDirtyRef.current = false;

                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `No Peanut Policy ${isEditMode ? 'updated' : 'created'} successfully!`
                });

                navigate('/dashboard/no-peanut-policy/list');
            }
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error || 'Failed to submit policy'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container>
            <Row>
                <Col sm="12">
                    <Card>
                        {remarks &&
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div className="header-title">
                                    <h4 className="card-title">
                                        {isEditMode ? 'Edit' : 'Sign'} {remarks}
                                    </h4>
                                </div>
                            </Card.Header>
                        }
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                {/* Policy Notice - a warm/urgent accent (not the app's usual
                                    purple section header) since this is a safety notice, not a
                                    regular form section. */}
                                <div className="mb-4 p-4" style={{ background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '12px' }}>
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '44px', height: '44px', backgroundColor: '#dc3545' }}>
                                            <i className="material-symbols-outlined">no_food</i>
                                        </div>
                                        <div>
                                            <h5 className="mb-0 fw-bold text-dark">No Peanut / Nut Policy</h5>
                                            <span className="text-muted" style={{ fontSize: '13px' }}>Please read carefully and sign below</span>
                                        </div>
                                    </div>

                                    <p className="mb-3" style={{ lineHeight: 1.7 }}><strong>Dear Parents,</strong></p>
                                    <p style={{ lineHeight: 1.7 }}>
                                        This letter is to inform you that a student/teacher in your child's classroom has a severe peanut/nut allergy.
                                        Strict avoidance of peanut/nut products is the only way to prevent a life-threatening allergic reaction.
                                        We are asking your assistance in providing the student with a safe learning environment.
                                    </p>
                                    <p style={{ lineHeight: 1.7 }}>
                                        If exposed to peanuts/nuts the student/teacher may develop a life-threatening allergic reaction that requires emergency medical treatment.
                                        The greatest potential for exposure at school is to peanut products and nut products. To reduce the risk of exposure,
                                        the classroom will be peanut/nut free. Please, do not send any peanut or tree nut containing products for your child to eat during snack in the classroom.
                                        Any exposure to peanuts or nuts through contact or ingestion can cause a severe reaction. If your child has eaten peanuts, peanut butter,
                                        or nuts prior to coming to school, please be sure your child's hands have been thoroughly washed prior to entering the school.
                                    </p>
                                    <p className="mb-4" style={{ lineHeight: 1.7 }}>
                                        We appreciate your support of these procedures. Please complete and return this form so that we are certain that every family has received this information.
                                        If you have any questions, please contact me.
                                    </p>

                                    <div className="text-center p-3 mb-3" style={{ backgroundColor: '#dc3545', borderRadius: '8px' }}>
                                        <span className="text-white fw-bold" style={{ letterSpacing: '0.04em' }}>NO PEANUTS OR NUTS, PLEASE</span>
                                    </div>

                                    <div className="d-flex align-items-center gap-2 pt-3" style={{ borderTop: '1px solid #f5c2c7' }}>
                                        <i className="material-symbols-outlined text-muted" style={{ fontSize: '20px' }}>badge</i>
                                        <div>
                                            <div className="fw-semibold" style={{ fontSize: '14px' }}>Mary Domotor</div>
                                            <div className="text-muted" style={{ fontSize: '12px' }}>Supervisor</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Acknowledgement */}
                                <div className="mb-4 p-3 d-flex align-items-start gap-2" style={{ background: '#f0f9f1', border: '1px solid #a8dab5', borderRadius: '10px' }}>
                                    <i className="material-symbols-outlined text-success flex-shrink-0" style={{ fontSize: '20px' }}>check_circle</i>
                                    <p className="mb-0" style={{ fontSize: '14px' }}>
                                        By signing below, I confirm that I have read and understand the peanut/nut free classroom procedures,
                                        and I agree to do my part in keeping the classroom peanut and nut free.
                                    </p>
                                </div>

                                {/* Signature Section */}
                                <div className="mb-2">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">draw</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Parent Signature</h5>
                                    </div>
                                    <Row className="mb-3">
                                        <Col md="12">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                                                <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px', borderRadius: '8px' }}>
                                                    <SignatureCanvas
                                                        ref={signaturePadRef}
                                                        clearOnResize={false}
                                                        onEnd={() => { signatureDirtyRef.current = true; }}
                                                        canvasProps={{
                                                            className: 'signature-canvas',
                                                            style: { width: '100%', height: '100%' }
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="mt-2"
                                                    onClick={handleClearSignature}
                                                    type="button"
                                                >
                                                    <i className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>ink_eraser</i>
                                                    Clear Signature
                                                </Button>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Signature Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={formData.parent_signature_date}
                                                    onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Submit Buttons */}
                                <div className="d-flex justify-content-end gap-2 pt-3 mt-2" style={{ borderTop: '1px solid #eef0f3' }}>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate('/dashboard/no-peanut-policy/list')}
                                        disabled={isSubmitting || submitLoading}
                                    >
                                        <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>close</i>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting || submitLoading}
                                    >
                                        {isSubmitting || submitLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>check_circle</i>
                                                {isEditMode ? 'Update' : 'Submit'} Policy
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};
export default NoPeanutPolicyFormComp;
